import { Order, OrderItem } from '../_interface/order';
import { FiscalDiscountMode } from '../_interface/company';
import { roundMoney, splitByWeight } from '../_helpers/money';
import { Payment, PaymentType } from '../_interface/payment';
import { InvoiceRequest, InvoiceType, Item, Payment as TaxCorePayment, PaymentType as TaxCorePaymentType, TransactionType } from '../_interface/taxCore';
import { CrossAppMessagingService } from './crossApp-messaging.service';
import { Injectable } from '@angular/core';
import { SessionService } from './session.service';
import { IMessage } from '../_interface/crossAppMessaging';
import { filter, firstValueFrom, pipe } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TaxCoreService {

  /** VPFR/PFX fiskalizacija uključena. App SettingsService je sinhronizuje iz
   *  `generateInvoiceWithVPFR`; core komponente (npr. order-details-dialog) čitaju ovo
   *  jer nemaju pristup app podešavanjima. */
  vpfrEnabled = false;

  constructor(private crossApp: CrossAppMessagingService,
    private session: SessionService
  ) { }

  async fiscalizeAsync(order: Order, payments: Payment[], buyerID?: string | null, buyerCostCenterId?: string | null) {
    return this.fiscalizeOrdersAsync([order], payments, buyerID, buyerCostCenterId);
  }

  /**
   * Fiskalizuje jednu ili više porudžbina kao JEDAN fiskalni račun (stavke iz svih
   * porudžbina se spajaju). Konobar ovo koristi pri naplati cele grupe stola;
   * `fiscalizeAsync` je prečica za jednu porudžbinu (npr. kiosk).
   */
  async fiscalizeOrdersAsync(orders: Order[], payments: Payment[], buyerID?: string | null, buyerCostCenterId?: string | null) {

    await firstValueFrom(this.session.products
      .pipe(
        filter(val => val.length > 0)
      )
    );

    var request: Partial<InvoiceRequest> = {
      invoiceType: InvoiceType.Normal,
      transactionType: TransactionType.Sale
    }

    // SUF identifikacija kupca ("<tip>:<vrednost>", npr. "10:108547119") — ide na fiskalni račun.
    if (buyerID) { request.buyerID = buyerID; }
    if (buyerCostCenterId) { request.buyerCostCenterId = buyerCostCenterId; }

    // Popust porudžbine (kupac/kupon) mora biti na računu, jer je naplata posle popusta — isto kao
    // serverska fiskalizacija (FiscalizationHost): proporcionalno na stavke ili kao „druga bezgotovinska".
    const discountAsPayment = this.session.companySettings.value?.fiscalDiscountMode === FiscalDiscountMode.OtherPayment;

    var items: Item[] = [];
    var discountPayment = 0;
    orders.forEach(o => {
      const lines = this.createItems(o, discountAsPayment);
      items.push(...lines);
      if (discountAsPayment && o.discountValue > 0) {
        // pune cene stavki − naplaćeni neto stavki (bez dostave, ona nije na računu)
        discountPayment += lines.reduce((s, l) => s + l.totalAmount, 0) - (o.totalWithDiscount - (o.deliveryFee ?? 0));
      }
    });
    request.items = items;

    request.payment = this.createPayments(payments);
    if (roundMoney(discountPayment) > 0) {
      request.payment.push({ amount: roundMoney(discountPayment), paymentType: TaxCorePaymentType.Other });
    }


    // Idempotentni ključ: deterministički iz orderID-jeva + plaćanja → retry posle transport
    // greške šalje ISTI RequestId, pa V-PFR ne pravi duplikat računa.
    const requestId = this.buildRequestId(orders, payments, buyerID, buyerCostCenterId);

    // requestId ide ODVOJENO od računa (data.invoice) — V-PFR ga prima kao "RequestId" header
    // (≤32 znaka); NE sme u telo računa.
    var msg: IMessage = {
      action: "taxCore.fiscalize",
      data: { invoice: request, requestId }
    }

    // Vraćamo i request i response (vpfrData) — POS dobija oba.
    return this.crossApp.sendRawMessage(msg, true, undefined, true)
      .then(response => {
        // Native vraća { error } kad fiskalizacija pukne, a bridge to RESOLVUJE (ne reject-uje).
        // Bez ovog throw-a naplata prolazi kao uspešna bez fiskalnog računa — sami prekidamo.
        if (response && response.error) {
          const err: any = new Error(response.error);
          err.fiscalizationFailed = true; // charge.component prikazuje prevedenu poruku
          throw err;
        }
        return { request, response };
      })
  }


  private createPayments(payments: Payment[]) {
    var list: TaxCorePayment[] = [];
    payments.forEach(p => {
      list.push({
        amount: p.amount,
        paymentType: this.convertToTaxCorePaymentType(p.type)
      });
    });

    return list;
  }

  convertToTaxCorePaymentType(type: PaymentType) {
    switch (type) {
      case PaymentType.None:
        return TaxCorePaymentType.Unknown;
      case PaymentType.Cash:
        return TaxCorePaymentType.Cash;
      case PaymentType.CreditCard:
        return TaxCorePaymentType.Card;
      case PaymentType.Instant:
        // map instant / mobile instant payments to MobileMoney
        return TaxCorePaymentType.MobileMoney;
      case PaymentType.Check:
        return TaxCorePaymentType.Check;
      case PaymentType.WireTransfer:
        return TaxCorePaymentType.WireTransfer;
      case PaymentType.Voucher:
        return TaxCorePaymentType.Voucher;
      case PaymentType.Other:
      case PaymentType.Presentation:
        return TaxCorePaymentType.Other;
      default:
        return TaxCorePaymentType.Other;
    }
  }

  /**
   * Fiskalne stavke porudžbine: aktivna (nestornirana) stavka po osnovnoj ceni + svaki plaćeni
   * modifikator kao zasebna stavka, sa količinom modifikatora × količina stavke (kao u zbiru
   * porudžbine, na serveru i na POS-u).
   *
   * Popust porudžbine: podrazumevano se raspoređuje na stavke (neto po stavci kao serverski
   * DiscountAllocator, pa po linijama stavke po udelu), tako da je zbir računa tačno naplaćeni
   * neto. `discountAsPayment` = stavke ostaju pune cene (na 2 decimale), a popust ide kao
   * zasebno plaćanje (dodaje ga pozivalac).
   */
  private createItems(order: Order, discountAsPayment: boolean) {
    const live = order.items.filter(i => !i.isCanceled);
    const lines: { item: Item, source: number }[] = [];

    live.forEach((i, index) => {
      var product = this.session.getProduct(i.productID);
      if (!product) {
        throw "Product not found";
      }

      lines.push({
        source: index,
        item: {
          name: i.name,
          quantity: i.quantity,
          labels: [product.taxLabel],
          unitPrice: i.price,
          totalAmount: i.price * i.quantity
        }
      });

      i.modifiers
        .filter(m => m.price > 0)
        .forEach(m => {
          var product = this.session.getProduct(m.productID);
          if (!product) {
            throw "Product not found";
          }

          const quantity = m.quantity * i.quantity;
          lines.push({
            source: index,
            item: {
              name: product.name,
              quantity: quantity,
              labels: [product.taxLabel],
              unitPrice: m.price,
              totalAmount: m.price * quantity
            }
          });

        });

    });

    const discount = order.discountValue ?? 0;
    if (discount > 0) {
      if (discountAsPayment) {
        lines.forEach(l => l.item.totalAmount = roundMoney(l.item.totalAmount));
      } else {
        this.applyDiscountToLines(live, lines, discount);
      }
    }

    return lines.map(l => l.item);
  }

  /** Neto po stavci (isto kao serverski DiscountAllocator), pa neto stavke po njenim linijama
   *  po bruto udelu (kao FiscalizationHost). Jedinična cena je samo informativna (porez se računa
   *  iz iznosa stavke), na 2 decimale kako PFR traži. */
  private applyDiscountToLines(live: OrderItem[], lines: { item: Item, source: number }[], discount: number) {
    const gross = live.map(i => i.quantity * (i.price + i.modifiers.reduce((s, m) => s + m.quantity * m.price, 0)));
    const subtotal = gross.reduce((s, g) => s + g, 0);
    const target = roundMoney(roundMoney(subtotal) - discount);

    let itemNet: number[];
    if (gross.some(g => g < 0)) {
      // retko (modifikator sa negativnom cenom): srazmerno, a razlika zaokruživanja na najveću stavku
      itemNet = gross.map(g => roundMoney(g * target / subtotal));
      const drift = roundMoney(target - itemNet.reduce((s, n) => s + n, 0));
      if (drift !== 0) {
        const largest = gross.reduce((best, g, k) => Math.abs(g) > Math.abs(gross[best]) ? k : best, 0);
        itemNet[largest] = roundMoney(itemNet[largest] + drift);
      }
    } else {
      itemNet = splitByWeight(target, gross);
    }

    itemNet.forEach((net, index) => {
      const itemLines = lines.filter(l => l.source === index);
      const shares = splitByWeight(net, itemLines.map(l => l.item.totalAmount));
      itemLines.forEach((l, k) => {
        l.item.totalAmount = shares[k];
        l.item.unitPrice = l.item.quantity ? roundMoney(shares[k] / l.item.quantity) : shares[k];
      });
    });
  }

  /** Deterministički idempotentni ključ za V-PFR "RequestId" header. Isti ulaz (iste
   *  porudžbine + plaćanja + kupac) → isti UUID, pa retry ne pravi duplikat fiskalnog računa.
   *  Promena plaćanja/kupca = drugi račun = drugi ključ (npr. split naplata). */
  private buildRequestId(orders: Order[], payments: Payment[], buyerID?: string | null, buyerCostCenterId?: string | null): string {
    const sig = [
      orders.map(o => o.orderID).sort((a, b) => a - b).join(','),
      buyerID ?? '',
      buyerCostCenterId ?? '',
      payments.map(p => p.type + ':' + p.amount).sort().join(',')
    ].join('|');

    const [a, b, c, d] = this.cyrb128(sig);
    const hex = (n: number) => (n >>> 0).toString(16).padStart(8, '0');
    // V-PFR "RequestId" header je max 32 znaka — vraćamo 32 hex cifre, bez crtica.
    return hex(a) + hex(b) + hex(c) + hex(d);
  }

  /** 128-bitni deterministički hash (cyrb128) — bez Web Crypto, radi i van secure-context-a u WebView-u. */
  private cyrb128(str: string): [number, number, number, number] {
    let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762;
    for (let i = 0, k = 0; i < str.length; i++) {
      k = str.charCodeAt(i);
      h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
      h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
      h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
      h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
    }
    h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
    h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
    h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
    h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
    h1 ^= (h2 ^ h3 ^ h4); h2 ^= h1; h3 ^= h1; h4 ^= h1;
    return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
  }
}
